import React, { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Link,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRouter } from 'next/router';
import { ImageSet } from 'server/config-cache/types';
import ImageDynamicSrc from 'src/components/ImageDynamicSrc';
import InterpolatedLinkTypography from 'src/components/InterpolatedLinkTypography';
import ConfigContext from 'src/context/config';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { object, string } from 'yup';

import {
  GET_STARTED_MOBILE_IMAGE_HEIGHT,
  RegexPatterns,
} from '../../../utils/constants';
import ProspectSignupForm from '../ProspectSignupForm';

type Props = {
  ctaScrollRef: React.RefObject<HTMLDivElement | null>;
  onSubmit: FormProps['onSubmit'];
  defaultFormData: FormProps['defaultFormData'];
  title?: string;
  subtitle?: string;
  info?: string;
  isLoading: boolean;
  isUserInContext?: boolean;
  errorText: string;
  setErrorText: (newErrorText: string) => void;
  showProspectForm: boolean;
};

const AboutYou: FC<Props> = ({
  ctaScrollRef,
  isUserInContext,
  isLoading,
  defaultFormData,
  onSubmit,
  info,
  subtitle,
  title,
  errorText,
  setErrorText,
  showProspectForm,
}) => {
  const theme = useTheme();
  const {
    getStartedPage: { aboutYouImg },
    // config has to be defined when we're in this context.
  } = React.useContext(ConfigContext)!;
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const hasImg = !!aboutYouImg;

  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
        backgroundColor: theme.palette.background.paper,
        padding: isSmallScreen ? '1rem' : 0,
      }}
    >
      <Grid
        size={{
          xs: 12,
          md: hasImg ? 12 : 8,
        }}
      >
        <Grid
          container
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid
            size={{
              xs: 12,
              md: hasImg ? 6 : 12,
            }}
          >
            <Grid
              container
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid>
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: isSmallScreen ? undefined : '1.5rem',
                  }}
                >
                  <Header title={title} subtitle={subtitle} />
                  {hasImg && isSmallScreen && (
                    <Box sx={{ marginBottom: '2rem' }}>
                      <GetStartedImage src={aboutYouImg} />
                    </Box>
                  )}
                  <div ref={ctaScrollRef}>
                    <Form
                      defaultFormData={defaultFormData}
                      onSubmit={onSubmit}
                      isUserInContext={isUserInContext}
                      isLoading={isLoading}
                      infoText={info}
                      setErrorText={setErrorText}
                      showProspectForm={showProspectForm}
                      errorText={errorText}
                    />
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          {hasImg && !isSmallScreen && <GetStartedImage src={aboutYouImg} />}
        </Grid>
      </Grid>
    </Grid>
  );
};

const GetStartedImage = ({ src: aboutYouImg }: { src: ImageSet }) => {
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  return (
    <Grid
      sx={{
        position: 'relative',
        // need to provide height after grid collapses to allow image to fill
        ...(isSmallScreen
          ? { height: GET_STARTED_MOBILE_IMAGE_HEIGHT }
          : { minHeight: '600px' }),
      }}
      size={{
        xs: 12,
        md: 6,
      }}
    >
      <ImageDynamicSrc
        src={aboutYouImg}
        layout="fill"
        priority
        quality={100}
        objectFit="cover"
        css={
          // image will collapse under form as screen size shrinks -- we want to round the right side of the img when side by side, and the bottom
          // when collapsed.
          isSmallScreen
            ? {
                borderRadius: '0.25rem',
              }
            : {
                borderBottomRightRadius: '0.25rem',
                borderTopRightRadius: '0.25rem',
              }
        }
        // @todo we might not want this / might want it configurable based on how it fits with other tenants
        // (makes the territory image look better and is only a small offest so might work fine.)
        objectPosition={isSmallScreen ? '50% 30%' : '50% 50%'}
        alt="about you image"
      />
    </Grid>
  );
};

const Header = ({ title, subtitle }: { title?: string; subtitle?: string }) => {
  const titleCharCount = title?.length;
  return (
    <Box sx={{ marginBottom: '2rem' }}>
      <Typography
        variant={titleCharCount && titleCharCount > 50 ? 'h4' : 'h3'}
        sx={{
          fontWeight: 500,
          lineHeight: 1,
          marginBottom: '1.25rem',
        }}
      >
        {title || <>Nice to meet you 👋</>}
      </Typography>
      <Typography variant={'body1'}>
        {subtitle || <>Who&apos;s going to be eating these meals?</>}
      </Typography>
    </Box>
  );
};

type FormProps = {
  infoText?: string;
  isLoading: boolean;
  defaultFormData: FormData;
  isUserInContext?: boolean;
  onSubmit: SubmitHandler<FormData>;
  showProspectForm: boolean;
  errorText: string;
  setErrorText: (newErrorText: string) => void;
};

const Form: FC<FormProps> = ({
  onSubmit,
  isUserInContext,
  isLoading,
  defaultFormData,
  showProspectForm,
  errorText,
  setErrorText,
}) => {
  const configContext = useNonNullContext(ConfigContext);
  const { termsAndPrivacy } = configContext.getStartedPage;
  const {
    control,
    formState: { isValid },
    handleSubmit,
    getValues,
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const theme = useTheme();
  const legalLinksStyles = {
    color: theme.palette.secondary.main,
    textDecoration: 'none',
  };
  const router = useRouter();
  const name = getValues('fullName');

  return (
    <FormControl fullWidth>
      <Controller
        control={control}
        name="fullName"
        defaultValue={defaultFormData.fullName}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextField
            label="Full name"
            variant="outlined"
            required
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            sx={{
              marginBottom: '1rem',
              backgroundColor: theme.palette.grey[50],
            }}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        defaultValue={defaultFormData.email}
        render={({ field: { onChange, value }, formState: { errors } }) =>
          isUserInContext ? (
            <></>
          ) : (
            <TextField
              label="Email"
              variant="outlined"
              required
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{
                marginBottom: '1rem',
                backgroundColor: theme.palette.grey[50],
              }}
              value={value}
              onChange={onChange}
            />
          )
        }
      />
      <Controller
        control={control}
        name="zipCode"
        defaultValue={defaultFormData.zipCode}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextField
            label="Zip code"
            variant="outlined"
            required
            error={!!errors.zipCode}
            helperText={errors.zipCode?.message}
            sx={{
              marginBottom: '3rem',
              backgroundColor: theme.palette.grey[50],
            }}
            value={value}
            onChange={(e) => {
              // clear error text on change to allow submission of form again after invalid submission.
              setErrorText('');

              /**
               * Cap input length at 5 and prevent non-numeric inputs (Chrome for example
               * will allow 'e' to be entered for scientific notation).
               */
              const val = e.currentTarget.value;
              if (
                val !== '' && // only short circuit if changing to a non-blank value so user can still clear input.
                isNaN(Number(val))
              ) {
                return false;
              }
              onChange(val);
            }}
          />
        )}
      />

      {showProspectForm && <ProspectSignupForm name={name} />}

      {/* Commented out for now upon product's request */}
      {/* <Alert
        variant="outlined"
        severity="info"
        sx={{ marginBottom: '2.25rem', textAlign: 'left' }}
      >
        {infoText || (
          <>
            We&apos;ll get you started with some hand-selected meals. As you
            adjust your menu each week, we learn your tastes and make smarter
            suggestions.
          </>
        )}
      </Alert> */}

      <Button
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        disabled={!isValid || isLoading || !!errorText}
      >
        Choose meals
      </Button>
      <Box sx={{ margin: '1.25rem 0 1rem 0' }}>
        <InterpolatedLinkTypography
          copyArray={termsAndPrivacy}
          linkStyles={legalLinksStyles}
          typographyProps={{
            variant: 'body2',
          }}
        />
      </Box>
      {!isUserInContext ? (
        <>
          <Box>
            <Typography variant="caption">Have an account?</Typography>
          </Box>
          <Link
            variant="caption"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: theme.typography.fontWeightBold,
              textDecoration: 'none',
              paddingRight: '0.25rem',
              cursor: 'pointer',
            }}
            onClick={() => router.push('/login')}
          >
            Log in to place a new order
          </Link>
        </>
      ) : null}
    </FormControl>
  );
};

export type FormData = {
  fullName: string;
  email: string;
  zipCode: string;
};

const schema = object({
  fullName: string().required('Full name required').defined().trim(),
  email: string()
    .email('Please enter a valid email')
    .required('Email required')
    .defined()
    .trim(),
  zipCode: string()
    .matches(RegexPatterns.ZIP_CODE, 'Please enter a valid zip code')
    .required('Zip code is required')
    .defined()
    .trim(),
});

export default AboutYou;
